/**
 * Runtime validation for the wire contract in ./wire.ts. Each schema is
 * checked against its interface with `satisfies`, so the published types and
 * what the gateway actually accepts cannot drift apart.
 */
import { z } from "zod";
import { linkInputSchema } from "./links";
import { LOCALES } from "./persona";
import { PHONE_PATTERN } from "./whatsapp";
import type { ChatRequest, ErrorCode, FeedbackRequest, LeadRequest, LinkClickRequest } from "./wire";

export * from "./wire";

export const API_VERSION = "v1";

export const chatRequestSchema = z.object({
  message: z.string().trim().min(1).max(4000),
  conversationId: z.string().regex(/^cnv_[A-Za-z0-9]{16,32}$/).optional(),
  visitorId: z.string().min(6).max(80).optional(),
  locale: z.enum(LOCALES).optional(),
  page: z
    .object({ url: z.string().max(500).optional(), title: z.string().max(200).optional() })
    .optional(),
  turnstileToken: z.string().max(4096).optional(),
  stream: z.boolean().optional(),
}) satisfies z.ZodType<ChatRequest>;

export const leadRequestSchema = z.object({
  conversationId: z.string().optional(),
  name: z.string().trim().min(2).max(80),
  whatsapp: z
    .string()
    .trim()
    .regex(PHONE_PATTERN, "Nomor WhatsApp tidak valid"),
  email: z.email().max(120).optional().or(z.literal("")),
  message: z.string().trim().max(1000).optional(),
  // The literal type makes "false" unrepresentable: no consent, no record (UU PDP).
  consent: z.literal(true, { error: "Persetujuan pemrosesan data wajib dicentang." }),
  source: z.enum(["chat", "form"]),
  page: z.string().max(500).optional(),
}) satisfies z.ZodType<LeadRequest>;

export const feedbackRequestSchema = z.object({
  messageId: z.string().regex(/^msg_[A-Za-z0-9]{16,32}$/),
  rating: z.enum(["up", "down"]),
}) satisfies z.ZodType<FeedbackRequest>;

export const linkClickRequestSchema = z.object({
  linkId: z.string().regex(/^lnk_[A-Za-z0-9]{16,32}$/),
  messageId: z.string().regex(/^msg_[A-Za-z0-9]{16,32}$/).optional(),
}) satisfies z.ZodType<LinkClickRequest>;

/** PUT /v1/links: a client's server keeps its referral list in step with its own catalogue. */
export const linksSyncRequestSchema = z.object({
  links: z.array(linkInputSchema).max(1000),
  mode: z.enum(["upsert", "replace"]).default("upsert"),
});

export const ERROR_CODES = [
  "invalid_request",
  "unauthorized",
  "forbidden",
  "origin_not_allowed",
  "not_found",
  "rate_limited",
  "message_too_long",
  "turnstile_failed",
  "balance_empty",
  "project_paused",
  "provider_error",
  "internal_error",
] as const satisfies readonly ErrorCode[];
